/**
 * AIT-505 — strip a URL fragment (`#...`) before it reaches Sentry.
 *
 * The public `/card` page reads its card-link token from
 * `window.location.hash` and immediately `history.replaceState`s it away
 * (PostHog's own capture is also gated off it via `disable_capture_url_hashes`),
 * but Sentry's browser SDK builds `request.url` / transaction names from
 * `window.location.href` at capture time — which can still carry the
 * fragment for an event fired before the replaceState runs. This is defense
 * in depth in `lib/sentry.ts`'s `beforeSend`/`beforeSendTransaction`, not the
 * primary guard.
 */
export function stripCardToken(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string' || url.length === 0) return url ?? undefined;
  const hashIndex = url.indexOf('#');
  return hashIndex === -1 ? url : url.slice(0, hashIndex);
}

/**
 * AIT-505 (Codex round 6, P1) — the third-party-script gate (main.tsx's
 * FirstPromoter init) and the Session Replay gate (lib/sentry.ts) both used
 * to compare `window.location.pathname === '/card'` directly. `/card/`
 * serves the exact same page (React Router doesn't distinguish) but failed
 * that strict check, so `/card/#crd_...` loaded the third-party fpr.js
 * script and started a Replay recording with the one-time token still sat in
 * the URL fragment. Normalize away trailing slashes before comparing so both
 * gates agree with each other and with the router.
 */
export function isCardLinkPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/card';
}
